import React, { useState } from 'react';
import cx from 'classnames';
import { LoaderBouncing } from '../Loaders';
import { getButtonSize } from './utils';
import './button.scss';
import { Icon } from '../Icons';

export type ButtonColor = 'primary' | 'red' | 'yellow';
export type ButtonSize = 's' | 'm' | 'l' | 'xl';

export interface ButtonProps {
  type?: 'submit' | 'reset' | 'button';
  size?: ButtonSize;
  color?: ButtonColor;
  onClick?: (event: React.MouseEvent) => unknown | Promise<unknown>;
  onMouseOver?: (event: React.MouseEvent) => unknown | Promise<unknown>;

  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
  style?: React.CSSProperties;
  styleContainer?: React.CSSProperties;

  isIconLeft?: boolean;
  withTriangle?: boolean;
  hasMobileSize?: boolean;
  loading?: boolean;
  Icon?: Icon.IconType;
  children?: React.ReactText | React.ReactElement;
  form?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children = 'Continue',
  type = 'button',
  onClick = () => {},
  onMouseOver = () => {},
  disabled = false,
  isIconLeft = false,
  color = 'primary',
  size = 'l',
  className,
  iconClassName,
  labelClassName,
  style = {},
  styleContainer = {},
  loading = false,
  withTriangle = false,
  Icon,
  form,
  hasMobileSize = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [buttonSize, _] = useState(getButtonSize(size, hasMobileSize));

  const handleButtonClick = async (event: React.MouseEvent) => {
    if (isLoading || disabled) {
      return;
    }

    try {
      const response = onClick(event);
      if (response instanceof Promise) {
        setIsLoading(true);
        await response;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderIcon = () => {
    if (!Icon) {
      return null;
    }
    return <Icon className={cx('icon', iconClassName)} />;
  };

  return (
    <button
      className={cx(
        'button-container',
        {
          'button-container__colorPrimary': color === 'primary',
          'button-container__colorRed': color === 'red',
          'button-container__colorYellow': color === 'yellow',
          'button-container__disabled': disabled,
          'button-container__reversed': isIconLeft,
          'button-container__s-triangled': buttonSize === 's' && withTriangle,
          'button-container__m-triangled': buttonSize === 'm' && withTriangle,
          'button-container__l-triangled': buttonSize === 'l' && withTriangle,
          'button-container__xl-triangled': buttonSize === 'xl' && withTriangle,
          'button-container__s': buttonSize === 's',
          'button-container__m': buttonSize === 'm',
          'button-container__l': buttonSize === 'l',
          'button-container__xl': buttonSize === 'xl',
          'button-container__loading': loading || isLoading,
        },
        className,
      )}
      disabled={disabled || isLoading || loading}
      form={form}
      style={style}
      type={type}
      onClick={handleButtonClick}
      onMouseOver={onMouseOver}
    >
      {renderIcon()}
      <span
        className={cx('button-container__label', labelClassName)}
        style={{ opacity: loading || isLoading ? 0 : 1, ...styleContainer }}
      >
        {children}
      </span>
      {withTriangle && <div className="triangle" />}
      {loading || isLoading ? (
        <div className="loadingContainer">
          <LoaderBouncing className="loaderBouncing" size={20} />
        </div>
      ) : null}
    </button>
  );
};
